import { Host, h } from "@stencil/core";
const FLOATING_POINT_ERROR = 1;
/**
 * @part actions - The container of the visible actions.
 * @part more-actions - The ch-dropdown control to show hidden actions when `itemsOverflowBehavior === "ResponsiveCollapse"`.
 *
 * @slot items - The slot for the actions.
 * @slot more-items - The slot for the hidden actions when `itemsOverflowBehavior === "ResponsiveCollapse"`.
 */
export class ChActionGroup {
    constructor() {
        this.displayedItems = -1;
        this.accessibleName = undefined;
        this.buttonLabel = "More actions";
        this.itemsOverflowBehavior = "ResponsiveCollapse";
        this.expandBehavior = "ClickOrHover";
        this.moreActionsButtonPosition = "Start";
        this.moreActionsDropdownPosition = "InsideStart_OutsideEnd";
        this.openOnFocus = false;
    }
    needForRAF = true; // To prevent redundant RAF (request animation frame) calls
    totalItems = -1;
    // Observer
    actionsContainerWatcher;
    actionsWatcher;
    // Refs
    actionsContainer;
    slotItems;
    el;
    handleOverflowBehaviorChange(newValue) {
        if (newValue !== "ResponsiveCollapse") {
            const actionGroupItems = this.slotItems.assignedElements();
            // Reset floating
            actionGroupItems.forEach(item => {
                item.floating = false;
            });
        }
        this.setResponsiveCollapse();
    }
    /**
     * Fired when the item is targeted or not.
     */
    displayedItemsCountChange;
    /**
     * Fired when the more actions button is expanded or collapsed.
     */
    moreActionsButtonExpandedChange;
    /**
     * Update the visibility of the actions.
     * Only works if itemsOverflowBehavior === "ResponsiveCollapse"
     */
    updateDisplayedActions = () => {
        const actionGroupItems = this.slotItems.assignedElements();
        // The column-gap property must be taken into account
        const columnGap = getComputedStyle(this.actionsContainer).columnGap;
        const columnGapValue = columnGap != null && columnGap.endsWith("px")
            ? Number(columnGap.replace("px", ""))
            : 0;
        // Since the last item does not add column-gap, we have to adjust the measurement
        let availableWidth = this.actionsContainer.clientWidth + columnGapValue - FLOATING_POINT_ERROR;
        let displayedItems = 0;
        // Check which items are visible
        actionGroupItems.forEach(action => {
            const actionWidth = action.clientWidth;
            availableWidth -= actionWidth + columnGapValue;
            if (availableWidth >= 0) {
                action.floating = false;
                displayedItems++;
            }
            else {
                action.floating = true;
            }
        });
        this.totalItems = actionGroupItems.length;
        this.displayedItems = displayedItems;
        this.displayedItemsCountChange.emit(displayedItems);
    };
    updateDisplayedActionInFrame = () => {
        if (!this.needForRAF) {
            return;
        }
        this.needForRAF = false; // No need to call RAF up until next frame
        requestAnimationFrame(() => {
            this.needForRAF = true; // RAF now consumes the movement instruction so a new one can come
            this.updateDisplayedActions();
        });
    };
    connectActionsObserver() {
        this.actionsWatcher = new ResizeObserver(this.updateDisplayedActionInFrame);
        // Observe the actions
        const actionGroupItems = this.slotItems.assignedElements();
        actionGroupItems.forEach(action => {
            this.actionsWatcher.observe(action);
        });
    }
    connectActionsContainerObserver() {
        this.actionsContainerWatcher = new ResizeObserver(this.updateDisplayedActionInFrame);
        this.actionsContainerWatcher.observe(this.actionsContainer);
    }
    disconnectActionsObserver() {
        if (this.actionsWatcher) {
            this.actionsWatcher.disconnect();
            this.actionsWatcher = null;
        }
    }
    disconnectActionsContainerObserver() {
        if (this.actionsContainerWatcher) {
            this.actionsContainerWatcher.disconnect();
            this.actionsContainerWatcher = null;
        }
    }
    setResponsiveCollapse() {
        this.disconnectActionsObserver();
        this.disconnectActionsContainerObserver();
        if (this.itemsOverflowBehavior !== "ResponsiveCollapse") {
            return;
        }
        this.connectActionsObserver();
        this.connectActionsContainerObserver();
    }
    updateActionsWatcher = () => {
        if (this.itemsOverflowBehavior !== "ResponsiveCollapse") {
            return;
        }
        // Avoid memory leaks by disconnecting and re-connecting the observer
        this.disconnectActionsObserver();
        this.connectActionsObserver();
    };
    handleMoreActionButtonExpand = (event) => {
        event.stopPropagation();
        this.moreActionsButtonExpandedChange.emit(event.detail);
    };
    componentDidLoad() {
        this.setResponsiveCollapse();
    }
    disconnectedCallback() {
        this.disconnectActionsObserver();
        this.disconnectActionsContainerObserver();
    }
    render() {
        // @todo TODO: Improve accessibility and keyboard navigation
        return (h(Host, { role: "menubar", "aria-label": this.accessibleName }, this.itemsOverflowBehavior === "ResponsiveCollapse" &&
            this.totalItems !== this.displayedItems && (h("ch-dropdown", { exportparts: "expandable-button:more-actions-button,separation:more-actions-separation,list:more-actions-list,section:more-actions-section,mask:more-actions-mask", buttonLabel: this.buttonLabel, class: "more-actions", part: "more-actions", expandBehavior: this.expandBehavior, openOnFocus: this.openOnFocus, position: this.moreActionsDropdownPosition, onExpandedChange: this.handleMoreActionButtonExpand }, h("slot", { name: "more-items", slot: "items" }))), h("div", { class: {
                actions: true,
                "actions--scroll": this.itemsOverflowBehavior === "AddScroll",
                "actions--multiline": this.itemsOverflowBehavior === "Multiline",
                "actions--responsive": this.itemsOverflowBehavior === "ResponsiveCollapse"
            }, part: "actions", ref: el => (this.actionsContainer = el) }, h("slot", { name: "items", onSlotchange: this.updateActionsWatcher, ref: el => (this.slotItems = el) }))));
    }
    static get is() { return "ch-action-group"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["action-group.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-group.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "buttonLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the label for the more actions button.\nImportant for accessibility."
                },
                "attribute": "button-label",
                "reflect": false,
                "defaultValue": "\"More actions\""
            },
            "itemsOverflowBehavior": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ItemsOverflowBehavior",
                    "resolved": "\"AddScroll\" | \"Multiline\" | \"ResponsiveCollapse\"",
                    "references": {
                        "ItemsOverflowBehavior": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-group/action-group/types.ts::ItemsOverflowBehavior"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute determines how items behave when the content of the ActionGroup overflows horizontal. This property is needed\nto make the control responsive to changes in the Width of the container of ActionGroup.\n\n| Value                 | Details                                                                                          |\n| --------------------- | ------------------------------------------------------------------------------------------------ |\n| `Add Scroll`          | The items of the ActionGroup that overflow horizontally are shown by means of a scroll.          |\n| `Multiline`           | The ActionGroup items that overflow horizontally are shown in a second line of the control.      |\n| `Responsive Collapse` | The Action Group items, when they start to overflow the control, are placed in the More Actions. |"
                },
                "attribute": "items-overflow-behavior",
                "reflect": false,
                "defaultValue": "\"ResponsiveCollapse\""
            },
            "expandBehavior": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"Click\" | \"ClickOrHover\"",
                    "resolved": "\"Click\" | \"ClickOrHover\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine which mouse actions on the expandable button display the dropdown\nsection."
                },
                "attribute": "expand-behavior",
                "reflect": false,
                "defaultValue": "\"ClickOrHover\""
            },
            "moreActionsButtonPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"Start\" | \"End\"",
                    "resolved": "\"End\" | \"Start\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "todo",
                            "text": "Check a better convention for this property, for example, \"ActionsInlineAlignment\"\nThis attribute determines the position of the More Actions button in the Action Group.\n\n| Value   | Details                                                               |\n| --------| --------------------------------------------------------------------- |\n| `Start` | The More Actions Button is displayed to the left of the ActionGroup.  |\n| `End`   | The More Actions Button is displayed to the right of the ActionGroup. |"
                        }],
                    "text": ""
                },
                "attribute": "more-actions-button-position",
                "reflect": false,
                "defaultValue": "\"Start\""
            },
            "moreActionsDropdownPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "DropdownPosition",
                    "resolved": "\"Center_OutsideEnd\" | \"Center_OutsideStart\" | \"InsideEnd_OutsideEnd\" | \"InsideEnd_OutsideStart\" | \"InsideStart_OutsideEnd\" | \"InsideStart_OutsideStart\" | \"OutsideEnd_Center\" | \"OutsideEnd_InsideEnd\" | \"OutsideEnd_InsideStart\" | \"OutsideEnd_OutsideEnd\" | \"OutsideEnd_OutsideStart\" | \"OutsideStart_Center\" | \"OutsideStart_InsideEnd\" | \"OutsideStart_InsideStart\" | \"OutsideStart_OutsideEnd\" | \"OutsideStart_OutsideStart\"",
                    "references": {
                        "DropdownPosition": {
                            "location": "import",
                            "path": "../../dropdown/types",
                            "id": "src/components/dropdown/types.ts::DropdownPosition"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the position of the dropdown section that is placed relative to\nthe more actions button."
                },
                "attribute": "more-actions-dropdown-position",
                "reflect": false,
                "defaultValue": "\"InsideStart_OutsideEnd\""
            },
            "openOnFocus": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the dropdowns should be opened when the action is focused."
                },
                "attribute": "open-on-focus",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "displayedItems": {}
        };
    }
    static get events() {
        return [{
                "method": "displayedItemsCountChange",
                "name": "displayedItemsCountChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is targeted or not."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }, {
                "method": "moreActionsButtonExpandedChange",
                "name": "moreActionsButtonExpandedChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the more actions button is expanded or collapsed."
                },
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "itemsOverflowBehavior",
                "methodName": "handleOverflowBehaviorChange"
            }];
    }
}
//# sourceMappingURL=action-group.js.map
